import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format an ISO timestamp to "Data as of 19 Apr 2026, 11:45 PM IST / 6:15 AM PST" */
function formatDual(isoStr) {
  if (!isoStr) return null;
  try {
    const utc = new Date(isoStr);
    // IST = UTC + 5:30
    const ist = new Date(utc.getTime() + 5.5 * 60 * 60 * 1000);
    // PST/PDT fixed at UTC − 7:00
    const pst = new Date(utc.getTime() - 7 * 60 * 60 * 1000);

    const day   = ist.getUTCDate();
    const month = MONTHS[ist.getUTCMonth()];
    const year  = ist.getUTCFullYear();

    let istH = ist.getUTCHours();
    const istM  = String(ist.getUTCMinutes()).padStart(2, '0');
    const istAp = istH >= 12 ? 'PM' : 'AM';
    istH = istH % 12 || 12;

    let pstH = pst.getUTCHours();
    const pstM  = String(pst.getUTCMinutes()).padStart(2, '0');
    const pstAp = pstH >= 12 ? 'PM' : 'AM';
    pstH = pstH % 12 || 12;

    // Append "(prev day)" when PST date is one day behind IST date
    const istKey = `${ist.getUTCFullYear()}-${ist.getUTCMonth()}-${ist.getUTCDate()}`;
    const pstKey = `${pst.getUTCFullYear()}-${pst.getUTCMonth()}-${pst.getUTCDate()}`;
    const prevDay = pstKey !== istKey ? ' (prev day)' : '';

    return `Data as of ${day} ${month} ${year}, ${istH}:${istM} ${istAp} IST / ${pstH}:${pstM} ${pstAp} PST${prevDay}`;
  } catch {
    return null;
  }
}

/**
 * ReportHeader — persistent header bar for every report page.
 *
 * Props:
 *   reportTitle  {string}   — shown on the left
 *   primaryTable {string}   — table name for last-refreshed query
 *   onChange     {function} — called with { fyLabel, startDate, endDate } whenever selection changes
 */
export function ReportHeader({ reportTitle, primaryTable, onChange }) {
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [fyLabel, setFyLabel]             = useState('');
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [loadingRefresh, setLoadingRefresh] = useState(false);

  // ── Load fiscal periods ──────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/fiscal-periods`)
      .then(({ data }) => {
        setFiscalPeriods(data);
        const current = data.find(fp => fp.is_current) || data[0];
        if (current) {
          setFyLabel(current.fy_label);
          setStartDate(current.fy_start);
          setEndDate(current.fy_end);
        }
      })
      .catch(() => {});
  }, []);

  // ── Load last-refreshed timestamp ────────────────────────────────────────
  useEffect(() => {
    if (!primaryTable) return;
    setLoadingRefresh(true);
    axios.get(`${API}/api/last-refreshed`, { params: { table: primaryTable } })
      .then(({ data }) => setLastRefreshed(data.last_updated))
      .catch(() => setLastRefreshed(null))
      .finally(() => setLoadingRefresh(false));
  }, [primaryTable]);

  // ── Notify parent on period change ───────────────────────────────────────
  const notify = useCallback(() => {
    if (fyLabel) onChange?.({ fyLabel, startDate, endDate });
  }, [fyLabel, startDate, endDate, onChange]);

  useEffect(() => { notify(); }, [notify]);

  // ── FY selector handler ───────────────────────────────────────────────────
  function handleFYChange(val) {
    if (val === 'All Time') {
      setFyLabel('All Time');
      setStartDate('');
      setEndDate('');
    } else if (val === 'Custom') {
      setFyLabel('Custom');
      // keep existing dates, make them editable
    } else {
      const fp = fiscalPeriods.find(f => f.fy_label === val);
      if (fp) {
        setFyLabel(fp.fy_label);
        setStartDate(fp.fy_start);
        setEndDate(fp.fy_end);
      }
    }
  }

  const isCustom  = fyLabel === 'Custom';
  const isAllTime = fyLabel === 'All Time';
  const isReadOnly = !isCustom;

  return (
    <div
      data-testid="report-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '10px 24px',
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {/* Report title */}
      <span
        data-testid="report-title"
        style={{
          fontWeight: 700,
          fontSize: 15,
          color: '#1e293b',
          flexShrink: 0,
          marginRight: 4,
        }}
      >
        {reportTitle}
      </span>

      {/* Divider */}
      <span style={{ color: '#e2e8f0', fontSize: 18, flexShrink: 0 }}>|</span>

      {/* Period label */}
      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500, flexShrink: 0 }}>
        Period:
      </span>

      {/* FY Selector */}
      <Select value={fyLabel} onValueChange={handleFYChange}>
        <SelectTrigger
          data-testid="fy-selector"
          style={{ width: 108, height: 30, fontSize: 13 }}
        >
          <SelectValue placeholder="Select FY" />
        </SelectTrigger>
        <SelectContent>
          {fiscalPeriods.map(fp => (
            <SelectItem key={fp.fy_label} value={fp.fy_label}>
              {fp.fy_label}
            </SelectItem>
          ))}
          <SelectItem value="All Time">All Time</SelectItem>
          <SelectItem value="Custom">Custom</SelectItem>
        </SelectContent>
      </Select>

      {/* Date inputs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          data-testid="start-date-input"
          type="date"
          value={startDate}
          readOnly={isReadOnly}
          disabled={isAllTime}
          onChange={e => isCustom && setStartDate(e.target.value)}
          style={{
            height: 30, fontSize: 12, padding: '0 8px',
            border: '1px solid #e2e8f0', borderRadius: 6,
            background: isReadOnly ? '#f8fafc' : '#fff',
            color: isAllTime ? '#94a3b8' : '#334155',
            cursor: isReadOnly ? 'default' : 'text',
            outline: 'none',
          }}
        />
        <span style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }}>→</span>
        <input
          data-testid="end-date-input"
          type="date"
          value={endDate}
          readOnly={isReadOnly}
          disabled={isAllTime}
          onChange={e => isCustom && setEndDate(e.target.value)}
          style={{
            height: 30, fontSize: 12, padding: '0 8px',
            border: '1px solid #e2e8f0', borderRadius: 6,
            background: isReadOnly ? '#f8fafc' : '#fff',
            color: isAllTime ? '#94a3b8' : '#334155',
            cursor: isReadOnly ? 'default' : 'text',
            outline: 'none',
          }}
        />
      </div>

      {/* Last refreshed — pushed to right */}
      <div
        data-testid="last-refreshed"
        style={{
          marginLeft: 'auto',
          fontSize: 11,
          color: '#94a3b8',
          flexShrink: 0,
          fontStyle: 'italic',
        }}
      >
        {loadingRefresh
          ? 'Checking data freshness…'
          : lastRefreshed
            ? formatDual(lastRefreshed)
            : primaryTable
              ? '—'
              : null
        }
      </div>
    </div>
  );
}
